import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { kb, type KBArticle } from "../../api/client";
import { useDuviCoreAdminQuery } from "../../hooks/use-api";
import { useDuviCoreAdminToast } from "../../components/duvi-core-admin-kit";

export function duviCoreAdminToDisplayLabel(category: string): string {
  return category
    ? category.charAt(0).toUpperCase() + category.slice(1)
    : category;
}

// A short plain-text preview derived from the markdown content.
export function duviCoreAdminArticlePreview(content: string): string {
  const text = (content || "")
    .replace(/^#+\s+/gm, "")
    .replace(/[*_`>#]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 180 ? `${text.slice(0, 180)}…` : text;
}

export function useDuviCoreAdminKnowledgeBaseList() {
  const navigate = useNavigate();
  const toast = useDuviCoreAdminToast();
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteArticleId, setDeleteArticleId] = useState<string | null>(null);

  const { data, isLoading, refetch } = useDuviCoreAdminQuery(
    () =>
      kb.list({
        category: selectedCategory || undefined,
        search: searchQuery || undefined,
      }),
    [selectedCategory, searchQuery],
  );

  const articles: KBArticle[] = data ?? [];

  const derivedCategories = useMemo(
    () => Array.from(new Set(articles.map((a) => a.category))).sort(),
    [articles],
  );

  const pendingDelete = articles.find((a) => a.article_id === deleteArticleId);

  const confirmDelete = async () => {
    if (!deleteArticleId) return;
    const title = pendingDelete?.title ?? "Article";
    await kb.delete(deleteArticleId);
    setDeleteArticleId(null);
    toast("success", "Article deleted", `“${title}” was removed.`);
    refetch();
  };

  return {
    articles,
    isLoading,
    derivedCategories,
    selectedCategory,
    setSelectedCategory,
    searchQuery,
    setSearchQuery,
    deleteArticleId,
    setDeleteArticleId,
    pendingDelete,
    confirmDelete,
    openNew: () => navigate("/knowledge-base/new"),
    openArticle: (id: string) => navigate(`/knowledge-base/${id}`),
  };
}
